import { useEffect, useRef, useCallback } from "react";

const getAllErrorPaths = (errors, parentPath = "") => {
  const paths = [];

  Object.keys(errors).forEach((key) => {
    const value = errors[key];
    const isArrayIndex = !isNaN(key);
    const currentPath = parentPath
      ? isArrayIndex
        ? `${parentPath}.[${key}]`
        : `${parentPath}.${key}`
      : key;

    if (typeof value === "object" && value !== null && !value.message) {
      paths.push(...getAllErrorPaths(value, currentPath));
    } else if (value?.message) {
      paths.push(currentPath);
    }
  });

  return paths;
};

const useFocusErrorForm = (formState = {}) => {
  const hasScrolledRef = useRef(false);
  const attemptsRef = useRef(0);
  const maxAttempts = 2;

  const findElement = useCallback((name) => {
    const selectors = [
      () => document.getElementById(name),
      () => document.querySelector(`[name="${name}"]`),
      () => document.querySelector(`[data-field="${name}"]`)
    ];

    for (const selector of selectors) {
      const element = selector();
      if (element) return element;
    }
    return null;
  }, []);

  const scrollToElement = useCallback((element) => {
    element.scrollIntoView({
      behavior: "smooth",
      block: "center",
    });

    setTimeout(() => {
      element.focus();
      hasScrolledRef.current = true;
      attemptsRef.current = 0;
    }, 300);
  }, []);

  useEffect(() => {
    const errorPaths = getAllErrorPaths(formState.errors || {});

    if (errorPaths.length === 0) {
      hasScrolledRef.current = false;
      attemptsRef.current = 0;
      return;
    }

    if (hasScrolledRef.current) return;

    const findAndScrollToElement = () => {
      attemptsRef.current++;

      const elements = errorPaths
        .map(findElement)
        .filter(Boolean);

      if (elements.length === 0 && attemptsRef.current < maxAttempts) {
        setTimeout(findAndScrollToElement, 100);
        return;
      }

      if (elements.length === 0) {
        hasScrolledRef.current = true;
        attemptsRef.current = 0;
        return;
      }

      const targetElement = elements.sort((a, b) => {
        const aRect = a.getBoundingClientRect();
        const bRect = b.getBoundingClientRect();
        return aRect.top - bRect.top;
      })[0];

      scrollToElement(targetElement);
    };

    attemptsRef.current = 0;
    const timeoutId = setTimeout(findAndScrollToElement, 100);

    return () => clearTimeout(timeoutId);
  }, [formState, findElement, scrollToElement]);

  useEffect(() => {
    hasScrolledRef.current = false;
    attemptsRef.current = 0;
  }, [formState.errors]);
};

export default useFocusErrorForm;
